<template>
  <v-container fluid class="ma-4 mt-3 px-8">
    <v-row justify="start" align="center">
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.year_batch"
          :items="batches"
          outlined
          dense
          hide-details="auto"
          label="Batch"
          @change="getPrograms()"
          clearable
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0" v-if="showPartner()">
        <v-autocomplete
          v-model="form.partner_id"
          :items="partners"
          item-text="name"
          item-value="id"
          outlined
          dense
          hide-details="auto"
          label="Partner"
          clearable
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.program_id"
          :items="programs"
          :item-text="
            (item) =>
              `${item.degree.short_name} ${
                item.specialization ? `- ${item.specialization}` : ''
              }`
          "
          item-value="id"
          outlined
          dense
          hide-details="auto"
          label="Programme"
          clearable
        ></v-autocomplete>
      </v-col>
      <v-col v-if="tab > 1" cols="12" md="3" class="py-0 my-0">
        <v-text-field
          v-model="form.application_id"
          outlined
          dense
          hide-details="auto"
          :rules="[rules.applicationNumber]"
          maxlength="15"
          label="Application ID"
          clearable
        ></v-text-field>
      </v-col>
      <v-col v-if="tab == 2" cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.option_selected"
          :items="['Direct', 'Loan', 'Installment']"
          outlined
          dense
          hide-details="auto"
          label="Method"
          clearable
        ></v-autocomplete>
      </v-col>
      <v-col v-if="tab == 2" cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.plan_selected"
          :items="feeTypes"
          outlined
          item-text="text"
          item-value="value"
          dense
          hide-details="auto"
          label="Type"
          clearable
        ></v-autocomplete>
      </v-col>
      <v-col v-if="tab == 3" cols="12" md="3" class="py-0 my-0">
        <v-menu
          v-model="menu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="displayDate"
              readonly
              outlined
              label="Date Range"
              dense
              v-bind="attrs"
              v-on="on"
              hide-details="auto"
            ></v-text-field>
          </template>
          <v-sheet
            class="pa-4"
            elevation="4"
            style="background-color: white; border-radius: 8px"
          >
            <v-row no-gutters>
              <v-col>
                <v-date-picker
                  v-model="selectedDates"
                  range
                  @input="onDateChange"
                ></v-date-picker>
              </v-col>

              <v-col class="pl-4 pt-4" style="max-width: 150px">
                <div
                  v-for="option in dateOptions"
                  :key="option.label"
                  class="mb-2 pt-2"
                >
                  <v-btn small color="accent" @click="setQuickDate(option)">
                    {{ option.label }}
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </v-sheet>
        </v-menu>
      </v-col>
      <v-col cols="12" md="3" class="d-flex justify-start">
        <v-btn
          class="accent white--text"
          :loading="loading"
          @click="init()"
          :disabled="loading"
        >
          Search
        </v-btn>
        <v-btn
          :disabled="
            (tab === 0 && !preApplicantPendings.length) ||
            (tab === 1 && !onlyAdvancePaidItems.length) ||
            (tab === 2 && !feeDuePendings.length) ||
            (tab === 3 && !fullyPaidItems.length)
          "
          class="accent white--text ml-2"
          @click="download()"
        >
          <v-icon>mdi-download</v-icon> Download
        </v-btn>
      </v-col>
    </v-row>
    <v-row class="d-flex justify-between mt-2">
      <v-col cols="11" md="11">
        <v-tabs
          v-model="tab"
          class="d-flex"
          grow
          @change="selectedApplicants = []"
        >
          <v-tab>Advance Tuition Fee Due</v-tab>
          <v-tab>Advance Tuition Fee Paid</v-tab>
          <v-tab>Balance Fee Due</v-tab>
          <v-tab>Fully Paid</v-tab>
        </v-tabs>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="1">
        <div class="d-flex justify-end">
          <v-btn
            small
            v-if="tab != 1 && tab != 3 && currentUser.role !== 'CFO'"
            fab
            :disabled="!selectedApplicants.length"
            class="accent white--text"
            @click="emailTriggerDialog = true"
          >
            <v-icon small>mdi-email</v-icon>
          </v-btn>
        </div>
      </v-col>
    </v-row>
    <v-tabs-items v-model="tab" class="mt-2">
      <v-tab-item>
        <v-row>
          <v-col cols="12">
            <v-sheet outlined>
              <v-data-table
                v-model="selectedApplicants"
                :headers="preApplicantPendingHeaders"
                :items="preApplicantPendings"
                fixed-header
                item-key="stn"
                :show-select="true"
                :show-select-all="true"
              >
                <template v-slot:[`item.partner_id`]="{ item }">
                  {{ getPartnerName(item.partner_id) }}
                </template>
                <template v-slot:[`item.program_id`]="{ item }">
                  {{ getProgramName(item.program_id) }}
                </template>
                <template v-slot:no-data>
                  <div class="text-center">No data available</div>
                </template>
              </v-data-table>
            </v-sheet>
          </v-col>
        </v-row>
      </v-tab-item>
      <v-tab-item>
        <v-row>
          <v-col cols="12">
            <v-sheet outlined>
              <v-data-table
                :headers="onlyAdvancePaidHeaders"
                :items="onlyAdvancePaidItems"
                fixed-header
                item-key="stn1"
              >
                <template v-slot:[`item.partner_id`]="{ item }">
                  {{ getPartnerName(item.partner_id) }}
                </template>
                <template v-slot:[`item.program_id`]="{ item }">
                  {{ getProgramName(item.program_id) }}
                </template>
                <template v-slot:[`item.payment_date`]="{ item }">
                  {{ formatDateTime(item.payment_date) }}
                </template>
                <template v-slot:no-data>
                  <div class="text-center">No data available</div>
                </template>
              </v-data-table>
            </v-sheet>
          </v-col>
        </v-row>
      </v-tab-item>
      <v-tab-item>
        <v-row>
          <v-col cols="12">
            <v-sheet outlined>
              <v-data-table
                v-model="selectedApplicants"
                :headers="feeDuePendingHeaders"
                :items="feeDuePendings"
                fixed-header
                item-key="application_id"
                :show-select="true"
                :show-select-all="true"
              >
                <template v-slot:[`item.action`]="{ item }">
                  <v-icon
                    v-if="item.plan_amount_paid > 0"
                    small
                    class="mr-2"
                    color="accent"
                    @click="viewPayments(item.id)"
                  >
                    mdi-eye
                  </v-icon>
                  <div v-else>-</div>
                </template>
                <template v-slot:[`item.partner_id`]="{ item }">
                  {{ getPartnerName(item.partner_id) }}
                </template>
                <template v-slot:[`item.program_id`]="{ item }">
                  {{ getProgramName(item.program_id) }}
                </template>
                <template v-slot:no-data>
                  <div class="text-center">No data available</div>
                </template>
              </v-data-table>
            </v-sheet>
          </v-col>
        </v-row>
      </v-tab-item>
      <v-tab-item>
        <v-row>
          <v-col cols="12">
            <v-sheet outlined>
              <v-data-table
                :headers="fullyPaidHeaders"
                :items="fullyPaidItems"
                fixed-header
                item-key="stn2"
              >
                <template v-slot:[`item.partner_id`]="{ item }">
                  {{ getPartnerName(item.partner_id) }}
                </template>
                <template v-slot:[`item.program_id`]="{ item }">
                  {{ getProgramName(item.program_id) }}
                </template>
                <template v-slot:[`item.action`]="{ item }">
                  <v-icon
                    small
                    class="mr-2"
                    color="accent"
                    @click="viewFullPayments(item.application_id)"
                  >
                    mdi-eye
                  </v-icon>
                </template>
                <template v-slot:no-data>
                  <div class="text-center">No data</div>
                </template>
              </v-data-table>
            </v-sheet>
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>
    <v-dialog v-model="paymentsDialog" max-width="800" persistent>
      <v-card elevation="0">
        <v-card-title>
          Payments
          <v-spacer></v-spacer>
          <v-btn
            small
            class="primary--text"
            icon
            @click="paymentsDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-sheet outlined>
                <v-data-table
                  :headers="paymentsHeaders"
                  :items="paymentsList"
                  hide-default-footer
                  fixed-header
                >
                  <template v-slot:[`item.finance_check`]="{ item }">
                    <v-chip
                      v-if="item.finance_check == 'Y'"
                      color="success"
                      small
                      class="white--text"
                    >
                      <v-icon small left>mdi-check-circle</v-icon>
                      Verified
                    </v-chip>
                    <v-chip v-else color="warning" small class="white--text">
                      <v-icon small left>mdi-alert-circle</v-icon>
                      Not Verified
                    </v-chip>
                  </template>
                  <template v-slot:[`item.transaction_id`]="{ item }">
                    {{ item.loan_utr ? item.loan_utr : item.transaction_id }}
                  </template>
                  <template v-slot:[`item.payment_date`]="{ item }">
                    {{
                      formatPaymentDate(item.realization_date) ||
                      formatPaymentDate(item.payment_date)
                    }}
                  </template>
                </v-data-table>
              </v-sheet>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="emailTriggerDialog" max-width="800">
      <v-card class="pa-2">
        <v-card-title>
          Trigger Email Notification to {{ selectedApplicants.length }}
          {{ selectedApplicants.length == 1 ? "Applicant" : "Applicants" }}
          <v-spacer></v-spacer>
          <v-btn
            small
            class="error--text"
            icon
            @click="emailTriggerDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="mt-2">
          <v-row>
            <v-col cols="7">
              <v-col cols="12">
                <v-menu
                  v-model="paymentDatePicker"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formattedPaymentEndDate"
                      label="Payment End Date"
                      append-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      outlined
                      dense
                      hide-details
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="triggerEmail.payment_end_date"
                    no-title
                    :min="minDate"
                    @input="paymentDatePicker = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="triggerEmail.subject"
                  label="Subject"
                  dense
                  hide-details
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="triggerEmail.description"
                  label="Body"
                  outlined
                  hide-details
                  dense
                ></v-textarea>
              </v-col>
            </v-col>
            <v-col cols="5">
              Preview:
              <v-card outlined class="pa-4">
                <h4 style="color: #3f51b5; margin-bottom: 5px">
                  <em>Greetings from SASTRA Deemed University!</em>
                </h4>

                <p>Dear *applicant_name*,</p>
                <p>
                  {{ triggerEmail.description || "........." }}
                </p>
                <p>
                  Last date for the payment:
                  {{ formattedPaymentEndDate || "*payment_end_date*" }}
                </p>

                <div style="font-size: 10px; color: #666">
                  <p>Regards,<br />SASTRA Online Education</p>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            :loading="loading"
            :disabled="
              !triggerEmail.subject ||
              !triggerEmail.description ||
              !triggerEmail.payment_end_date
            "
            @click="triggerPendingApplicantEmail"
            >Send Email</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { rules } from "@/helpers/rules";
import {
  feeDuePendingHeaders,
  paymentsHeaders,
  preApplicantPendingHeaders,
  onlyAdvancePaidHeaders,
  fullyPaidHeaders,
} from "@/constants/headers";
import { feeTypes } from "@/constants/enum";

export default {
  components: {},
  data() {
    return {
      fullyPaidHeaders,
      feeDuePendingHeaders,
      tab: 0,
      form: {},
      programs: [],
      partners: [],
      rules,
      paymentsDialog: false,
      paymentsList: [],
      paymentsHeaders,
      selectedApplicants: [],
      emailTriggerDialog: false,
      paymentDatePicker: false,
      triggerEmail: {
        payment_end_date: null,
        subject: "",
        description: "",
      },
      loading: false,
      preApplicantPendingHeaders,
      preApplicantPendings: [],
      fullyPaidItems: [],
      feeDuePendings: [],
      feeTypes,
      onlyAdvancePaidHeaders,
      onlyAdvancePaidItems: [],
      batches: [],
      menu: false,
      displayDate: "",
      selectedDates: [],
      dateOptions: [
        { label: "Today", days: 0 },
        { label: "Yesterday", type: "yesterday" },
        { label: "This Week", type: "week" },
        { label: "Last 7 Days", days: 6 },
        { label: "This Month", type: "month" },
        { label: "Last 30 Days", days: 29 },
        { label: "Reset", type: "reset" },
      ],
    };
  },
  watch: {
    tab: {
      handler() {
        this.form = {};
        this.$set(this.form, "year_batch", this.batches[0]);
        this.displayDate = "";
        this.selectedDates = [];
        this.init();
      },
    },
  },
  computed: {
    formatPaymentDate() {
      return (date) => {
        if (!date) return null;
        const [year, month, day] = date.split("-");
        return `${day}-${month}-${year}`;
      };
    },
    formatDate() {
      return (date) => {
        let newDate = new Date(date);

        const day = String(newDate.getDate()).padStart(2, "0");
        const month = String(newDate.getMonth() + 1).padStart(2, "0");
        const year = newDate.getFullYear();
        return `${day}-${month}-${year}`;
      };
    },
    formattedPaymentEndDate() {
      const date = this.triggerEmail?.payment_end_date;
      if (!date) return "";
      const [year, month, day] = date.split("-");
      this.triggerEmail.payment_end_date = `${year}-${month}-${day}`;
      return `${day}-${month}-${year}`;
    },
    minDate() {
      return new Date().toISOString().substr(0, 10);
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    formatDateBackend() {
      return (date) => {
        let newDate = new Date(date);

        const day = String(newDate.getDate()).padStart(2, "0");
        const month = String(newDate.getMonth() + 1).padStart(2, "0");
        const year = newDate.getFullYear();
        return `${year}-${month}-${day}`;
      };
    },
  },
  async created() {
    this.$store.commit("setBreadcrumbs", [
      {
        text:
          this.currentUser.role === "CFO"
            ? "Collections Report"
            : this.$route.name,
      },
    ]);
    await this.generateBatchList();
    await this.showPartnerFilter();
    if (this.currentUser.partner_id != 1) {
      this.form.partner_id = this.currentUser.partner_id;
    }
    await this.getPrograms();
    this.init();
    this.getEmailTriggerConfig();
  },
  methods: {
    formatDisplayDate(from, to) {
      return `${this.formatDate(from)}~${this.formatDate(to)}`;
    },
    onDateChange() {
      if (this.selectedDates.length === 2) {
        const [fromDate, toDate] = this.selectedDates;

        this.form.fully_paid_from_date = this.formatDateBackend(fromDate);
        this.form.fully_paid_to_date = this.formatDateBackend(toDate);

        this.displayDate = this.formatDisplayDate(fromDate, toDate);
      }
    },
    setQuickDate(option) {
      if (option.type === "reset") {
        this.form.fully_paid_from_date = null;
        this.form.fully_paid_to_date = null;
        this.displayDate = "";
        this.selectedDates = [];
        return;
      }
      const today = new Date();
      let fromDate = new Date(today);
      let toDate = new Date(today);

      if (option.type === "week") {
        fromDate.setDate(today.getDate() - today.getDay());
      } else if (option.type === "month") {
        fromDate.setDate(1);
      } else if (option.type === "yesterday") {
        fromDate.setDate(today.getDate() - 1);
        toDate.setDate(today.getDate() - 1);
      } else if (option.days) {
        fromDate.setDate(today.getDate() - option.days);
      }

      this.selectedDates = [
        this.formatDateBackend(fromDate),
        this.formatDateBackend(toDate),
      ];

      this.onDateChange();
    },
    async viewPayments(student_admit_plan_id) {
      const res = await this.$axios.get(
        `/student_admit_plan_payments?student_admit_plan_id=${student_admit_plan_id}`,
      );
      const payments = res.data.data.sort((a, b) => a.id - b.id);

      this.paymentsList = [...payments];
      this.paymentsDialog = true;
    },
    async viewFullPayments(application_id) {
      const res = await this.$axios.get(
        `/get_fully_paid_payments?application_id=${application_id}`,
      );
      const payments = res.data.data.sort((a, b) => a.id - b.id);

      this.paymentsList = [...payments];
      this.paymentsDialog = true;
    },
    async getFullyPaid() {
      const queryString = this.objectToQueryParams({
        ...this.form,
      });
      const response = await this.$axios.get(
        `/get_fully_paid_report?${queryString}&!usn_id=true&!plan_amount_paid=0`,
      );

      this.fullyPaidItems = response.data.data;
    },
    async getPreApplicantPendings() {
      const queryString = this.objectToQueryParams({
        ...this.form,
      });
      const response = await this.$axios.get(
        `/pre_applications?${queryString}&advance_fee_paid=N`,
      );

      this.preApplicantPendings = response.data.data;
    },
    async getFeeDuePendings() {
      const queryString = this.objectToQueryParams({
        ...this.form,
      });
      const response = await this.$axios.get(
        `/student_admit_plans?${queryString}&!plan_amount_balance=0`,
      );

      this.feeDuePendings = response.data.data;
    },
    async getPreApplicantPaidOnly() {
      const queryString = this.objectToQueryParams({
        ...this.form,
      });
      const response = await this.$axios.get(
        `/get_only_advance_paid?${queryString}&advance_fee_paid=Y`,
      );

      this.onlyAdvancePaidItems = response.data.data.sort(
        (a, b) => b.applicant_detail_id - a.applicant_detail_id,
      );
    },
    objectToQueryParams(obj) {
      if (this.currentUser.partner_id != 1) {
        obj.partner_child_id = this.currentUser.partner_id;
      }
      if (obj.year_batch) {
        const [year, batch] = obj.year_batch.split("-");
        obj.year = year;
        obj.batch = batch;
        delete obj.year_batch;
      }
      if (obj.partner_id == 1) {
        delete obj.partner_id;
      }
      return Object.keys(obj)
        .filter(
          (key) =>
            obj[key] !== undefined && obj[key] !== null && obj[key] !== "",
        ) // Remove undefined/null values
        .map(
          (key) => encodeURIComponent(key) + "=" + encodeURIComponent(obj[key]),
        )
        .join("&");
    },
    async init() {
      this.loading = true;
      if (this.tab == 0) {
        this.getPreApplicantPendings();
      } else if (this.tab == 1) {
        this.getPreApplicantPaidOnly();
      } else if (this.tab == 2) {
        this.getFeeDuePendings();
      } else if (this.tab == 3) {
        this.getFullyPaid();
      }
      this.loading = false;
    },
    async getPrograms() {
      let endpoint = "/programs";
      if (
        this.currentUser.role === "Business" ||
        this.currentUser.role === "Business Reviewer" ||
        this.currentUser.role === "Coordinator" ||
        this.currentUser.role === "Partner"
      ) {
        this.form.partner_id =
          this.form.partner_id ?? this.currentUser.partner_id;
        endpoint = `/get_partner_programs/${this.form.partner_id}`;
      }
      if (this.currentUser.partner_id == 1) {
        const [year, batch] = this.form.year_batch.split("-");
        endpoint = `/get_batch_programs/${year}/${batch}`;
      }
      try {
        const response = await this.$axios.get(endpoint);
        this.programs = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    showPartner() {
      return (
        (this.currentUser.role === "Business" &&
          this.partners.length > 1 &&
          this.currentUser.partner_level != "S") ||
        this.currentUser.partner_id == 1
      );
    },
    getProgramName(id) {
      const program = this.programs.find((p) => p.id === id);
      return program
        ? program.degree.short_name +
            (program.specialization ? " - " + program.specialization : "")
        : "";
    },
    async getPartners() {
      try {
        const response = await this.$axios.get("/partners");
        this.partners = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    getPartnerName(id) {
      const partner = this.partners.find((partner) => partner.id === id);
      return partner ? partner.name : "";
    },
    getEmailTriggerConfig() {
      this.$axios.get("/email_trigger").then((res) => {
        this.triggerEmail = {
          payment_end_date: null,
          subject: res.data.data?.subject || "",
          description: res.data.data?.description || "",
        };
      });
    },
    triggerPendingApplicantEmail() {
      this.loading = true;
      const pre_applicants = this.selectedApplicants
        .filter((item) => !item.application_id)
        .map((item) => item.pre_applicant_id);
      const applicants = this.selectedApplicants
        .filter((item) => item.application_id)
        .map((item) => item.applicant_detail_id);
      this.$axios
        .post("/trigger_pending_applicant_email", {
          pre_application_ids: pre_applicants,
          applicant_ids: applicants,
          email_trigger_data: this.triggerEmail,
        })
        .then(() => {
          this.loading = false;
          this.emailTriggerDialog = false;
          this.$store.commit("showSnackbar", {
            message: "Email triggered successfully",
            color: "success",
          });
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async download() {
      const endpoints = [
        "download_advance_tuition_fee_pending",
        "download_advance_tuition_fee_paid",
        "download_balance_fee_due",
        "download_fully_paid",
      ];
      const endpoint = endpoints[this.tab];
      const [, ...words] = endpoint.split("_");
      const fileName = words
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
      const queryString = this.objectToQueryParams({
        ...this.form,
      });

      this.$axios
        .get(`${endpoint}?${queryString}`, {
          responseType: "blob",
        })
        .then((res) => {
          let blob = new Blob([res.data], { type: "text/csv" }),
            url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `${fileName}.csv`);
          document.body.appendChild(link);
          link.click();
        });
    },
    async showPartnerFilter() {
      await this.getPartners();
      if (
        this.currentUser.role === "Business" &&
        this.currentUser.partner_level === "P"
      ) {
        this.partners = this.partners.filter(
          (item) =>
            item.id === this.currentUser.partner_id ||
            item.parent_id === this.currentUser.partner_id,
        );
      }
    },
    async generateBatchList() {
      try {
        const response = await this.$axios.get("/get_year_batch");
        this.batches = response.data.data;
        this.form.year_batch = this.batches[0];
      } catch (error) {
        console.log(error);
      }
    },
    formatDateTime(dateStr) {
      const date = new Date(dateStr);
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      const hh = String(date.getHours()).padStart(2, "0");
      const mi = String(date.getMinutes()).padStart(2, "0");
      const ss = String(date.getSeconds()).padStart(2, "0");

      return `${dd}-${mm}-${yyyy} ${hh}:${mi}:${ss}`;
    },
  },
};
</script>

<style scoped>
.v-data-table::v-deep th {
  padding-right: 0px !important;
}
.v-data-table::v-deep td {
  font-size: 13px !important;
  padding-right: 0px !important;
}
</style>
